import { Button, Flex, InputNumber } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import StatusComponent from 'components/status/status';
import Icon from 'utils/icon';
import Riyal from 'components/riyal/riyal';
import { formatWithCommas, parseWithoutCommas } from 'components/formNumberElement/formNumberElement';
import DisabledElement from 'components/disabledElement/disabledElement';

interface SharePriceInputComponentProps {
	availableShares: number;
	disabledSharePrice?: boolean;
	sharePrice?: number;
	onChange(value: { count?: number; price?: number }): void;
}

const SharePriceInputComponent: FunctionComponent<SharePriceInputComponentProps> = ({
	availableShares,
	disabledSharePrice,
	sharePrice = 0,
	onChange,
}) => {
	const { t } = useTranslation();

	const [count, setCount] = useState<number>(0);
	const [price, setPrice] = useState<number>(sharePrice);

	useEffect(() => {
		onChange({ price });
	}, [price]);

	useEffect(() => {
		onChange({ count });
	}, [count]);

	const modifyCount = (operation: 'plus' | 'minus') => {
		if (operation === 'plus') {
			setCount(+count + 1);
		} else {
			if (count > 0) setCount(+count - 1);
		}
	};

	const validateCount = (value: number | string | null) => {
		setCount(Math.min(Number(value) || 0, availableShares));
	};

	return (
		<div style={{ width: 'max-content' }}>
			<Flex className="mb-2" align="center" gap={20}>
				<h6 className="mb-0">{t('COMMON.FREE_SHARES')}</h6>
				<StatusComponent
					text={`${(availableShares - count).toLocaleString()}`}
					color="green"
					small
					iconName="energy"
					iconHeight={15}
					iconWidth={10}
				/>
			</Flex>
			<Flex gap={20} className="p-3 containerDiv" style={{ background: '#fafafa' }}>
				<Flex vertical justify="center">
					<p className="mb-2 fw-medium text-center">{t('ACTIONS.SHARES_COUNT')}</p>
					<Flex gap={10} align="center" style={{ minHeight: 40 }}>
						<Button type="primary" className="h-max" style={{ padding: 7 }} onClick={() => modifyCount('minus')} disabled={count === 0}>
							<Icon name="minus" color="#fff" width={12} height={12} />
						</Button>

						<InputNumber
							variant="borderless"
							className="inputCentered"
							controls={false}
							min={0}
							step={1}
							precision={0}
							max={availableShares}
							style={{ width: 100, padding: 0 }}
							value={count}
							formatter={formatWithCommas}
							parser={(value) => Number(parseWithoutCommas(value))}
							onChange={validateCount}
						/>

						<Button type="primary" className="h-max" style={{ padding: 7 }} onClick={() => modifyCount('plus')} disabled={count == availableShares}>
							<Icon name="plus" color="#fff" width={12} height={12} />
						</Button>
					</Flex>
				</Flex>
				<div style={{ width: 2, background: '#fff' }} />
				<Flex vertical justify="center">
					<p className="mb-2 fw-medium text-center">{t('ACTIONS.SHARE_PRICE')}</p>
					{disabledSharePrice ? (
						<DisabledElement value={price} showPrice style={{ minWidth: 130 }} />
					) : (
						<InputNumber
							type="number"
							value={price}
							onChange={(value) => setPrice(value as number)}
							variant="filled"
							min={0}
							defaultValue={0}
							className="white borderless"
							step={1}
							precision={2}
							addonBefore={<Riyal size={16} />}
							style={{ width: 130, minHeight: 40, direction: 'ltr' }}
						/>
					)}
				</Flex>
			</Flex>
		</div>
	);
};

export default SharePriceInputComponent;
